<template>
    <!-- Caja de reserva de la ficha (diseño nuevo). Misma lógica que antes:
         precio → fechas → Reservar → "Resérvalo por" → Solicitar visita →
         Escríbele al anfitrión. Las fechas se guardan en el mismo store
         (search + simulator) que usaba el DatePicker. -->
    <div class="fx-bk accommodation-container">

        <!-- Precio -->
        <div class="fx-bk-price">
            <Price :accommodation="accommodation" :monthlyPrice="monthlyPrice" :dailyPrice="dailyPrice" />
        </div>

        <!-- Fechas -->
        <div v-if="loadingCalendar" class="fx-bk-spin" :aria-label="$t('ficha_nueva.loading_dates')"><span></span></div>

        <!-- Homiii: contratos de meses completos (mes de entrada y de salida). -->
        <div v-else-if="isHomiiiHost" class="fx-bk-homiii">
            <HomiiiMonthPicker :minMonths="homiiiMinMonths" :occupiedUntil="homiiiOccupiedUntil" />
        </div>

        <template v-else>
            <!-- Ocupado hasta una fecha futura: sin este aviso el calendario se ve bloqueado sin explicación. -->
            <p v-if="disponibleDesdeTexto" class="fx-bk-note">{{ $t('ficha_nueva.available_from', { date: disponibleDesdeTexto }) }}</p>

            <div :class="['fx-bk-dates', { 'is-open': calAbierto }]" role="group" :aria-label="$t('ficha_nueva.dates')">
                <button type="button" :class="['fx-bk-date', { 'is-active': calAbierto && !checkInDate }]" :aria-expanded="String(calAbierto)" aria-controls="fx-bk-cal" @click="calAbierto = !calAbierto">
                    <span class="fx-bk-date-k">{{ $t('ficha_nueva.check_in') }}</span>
                    <span :class="['fx-bk-date-v', { 'is-set': !!checkInDate }]">{{ checkInDate ? fechaLarga(checkInDate) : $t('ficha_nueva.add_date') }}</span>
                </button>
                <button type="button" :class="['fx-bk-date', { 'is-active': calAbierto && checkInDate && !checkOutDate }]" :aria-expanded="String(calAbierto)" aria-controls="fx-bk-cal" @click="calAbierto = !calAbierto">
                    <span class="fx-bk-date-k">{{ $t('ficha_nueva.check_out') }}</span>
                    <span :class="['fx-bk-date-v', { 'is-set': !!checkOutDate }]">{{ checkOutDate ? fechaLarga(checkOutDate) : $t('ficha_nueva.add_date') }}</span>
                </button>
            </div>

            <div v-if="calAbierto" id="fx-bk-cal" class="fx-bk-cal">
                <FxCalendario
                    :disabledDates="disabledDates"
                    :minNights="Number(minNights) || 1"
                    :checkIn="checkInDate"
                    :checkOut="checkOutDate"
                    @change="alCambiarFechas"
                />
            </div>

            <p class="fx-bk-min">
                <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                {{ $t("accommodation.min-nights", { nights: minNights }) }}
            </p>
        </template>

        <!-- Desglose (al elegir las fechas, antes de Reservar) -->
        <div class="fx-bk-sim">
            <Simulator class="simulator" :moneda="currency" />
        </div>

        <!-- Reservar -->
        <div class="fx-bk-reserve">
            <Pay
                class="reserve-button"
                v-on:canceled="cancelReservation(reservation.idreserva)"
                :image="image"
                :moneda="currency"
                :guest="guest"
            />
        </div>

        <div class="fx-bk-btns">
            <Button @click="isLoggedIn ? handleInitConversation() : loginBeforeSendMEssage()" class="new-btn init-conversation" variant="primary">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5.5h16a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5H9l-4.5 3.5V17.5H4A1.5 1.5 0 0 1 2.5 16V7A1.5 1.5 0 0 1 4 5.5Z" /></svg>
                {{ $t('ficha_nueva.write_host') }}
            </Button>
            <Visit class="visit-button fx-bk-visit" :accommodation="accommodation" :isLoggedIn="isLoggedIn" @request-visit="handleVisitRequest" />
        </div>

        <p class="fx-bk-safe">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.4 7.5 9.5 4.4-1.1 7.5-4.9 7.5-9.5V6L12 3Z" /><path d="m9 12 2 2 4-4" /></svg>
            {{ $t('ficha_nueva.safe_payment') }}
        </p>

        <!-- Solicitar visita (formulario en un modal ancho) -->
        <b-modal
            v-model="showVisitModal"
            modal-class="fx-modal fx-modal--vis"
            hide-header
            hide-footer
            centered
            size="xl"
            :no-close-on-backdrop="true"
            title=""
        >
            <RequestAccommodationVisit
                :accommodationId="accommodation && accommodation.idalojamiento ? accommodation.idalojamiento : $route.params.idalojamiento"
                :inModal="true"
                @success="handleVisitSuccess"
                @close="showVisitModal = false"
            />
        </b-modal>

        <InitConversationModal :visible="visible" :accommodation="accommodation" @hide="visible = false" />

        <!-- Avisos (mismos que antes, con el diseño nuevo) -->
        <b-modal v-model="showDateAlert" modal-class="fx-modal" hide-header hide-footer centered size="sm">
            <div class="fx-al">
                <div class="fx-al-ico" aria-hidden="true">!</div>
                <p class="fx-al-t">{{ $t('ficha_nueva.dates_needed_title') }}</p>
                <p class="fx-al-txt">{{ $t('conversation.select-dates-required') }}</p>
                <div class="fx-al-acts"><button type="button" class="fx-btn fx-btn--primary" @click="showDateAlert = false; calAbierto = true">{{ $t('ficha_nueva.pick_dates_btn') }}</button></div>
            </div>
        </b-modal>

        <b-modal v-model="showIcalAlert" modal-class="fx-modal" hide-header hide-footer centered size="sm">
            <div class="fx-al">
                <div class="fx-al-ico fx-al-ico--bad" aria-hidden="true">!</div>
                <p class="fx-al-txt">{{ $t('conversation.no-ical-accommodation') }}</p>
                <div class="fx-al-acts"><button type="button" class="fx-btn fx-btn--primary" @click="showIcalAlert = false">{{ $t('conversation.close') }}</button></div>
            </div>
        </b-modal>

        <b-modal v-model="showPhoneAlert" modal-class="fx-modal" hide-header hide-footer centered size="sm">
            <div class="fx-al">
                <div class="fx-al-ico" aria-hidden="true">☎</div>
                <p class="fx-al-txt">{{ phoneAlertMessage }}</p>
                <div class="fx-al-acts">
                    <button type="button" class="fx-btn fx-btn--ghost" @click="showPhoneAlert = false">{{ $t('ficha_nueva.not_now') }}</button>
                    <button type="button" class="fx-btn fx-btn--primary" @click="redirectToProfile">{{ $t('conversation.continue') || 'Continuar' }}</button>
                </div>
            </div>
        </b-modal>

        <b-modal v-model="showAvailabilityPopup" modal-class="fx-modal" hide-header hide-footer centered size="sm" no-close-on-backdrop no-close-on-esc>
            <div class="fx-al">
                <div v-if="availabilityPopupIcon === 'success'" class="fx-al-ico fx-al-ico--ok" aria-hidden="true">✓</div>
                <div v-else-if="String(availabilityPopupIcon).indexOf('fa-spin') >= 0" class="fx-bk-spin" style="height:70px"><span></span></div>
                <div v-else class="fx-al-ico fx-al-ico--bad" aria-hidden="true">!</div>
                <p class="fx-al-txt">{{ availabilityPopupMessage }}</p>
                <div class="fx-al-acts"><button type="button" class="fx-btn fx-btn--ghost" @click="closeAvailabilityPopup">{{ $t('conversation.close') }}</button></div>
            </div>
        </b-modal>

        <b-modal v-model="showSuccessPopup" modal-class="fx-modal" hide-header hide-footer centered size="sm" no-close-on-backdrop no-close-on-esc>
            <div class="fx-al">
                <div class="fx-al-ico fx-al-ico--ok" aria-hidden="true">✓</div>
                <p class="fx-al-t">{{ successPopupMessage }}</p>
                <p class="fx-al-txt">{{ $t('ficha_nueva.going_to_messages') }}</p>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import HotelDatePicker from "vue-hotel-datepicker";
import Pay from "@/components/Payment/Pay";
import axios from "axios";
import { calculateOccupiedDays } from "@/Utils/Utils";
import priceMixin from "@/mixins/priceMixin";
import Guests from "@/components/DetalleAlojamiento/subcomponents/Guests.vue";
import Price from "@/components/DetalleAlojamiento/subcomponents/Price.vue";
import Simulator from "@/components/DetalleAlojamiento/subcomponents/Simulator.vue";
import Visit from "@/components/DetalleAlojamiento/subcomponents/Visit.vue";
import DatePicker from "@/new-components/DatePicker/DatePicker.vue";
import HomiiiMonthPicker from "@/new-components/DatePicker/HomiiiMonthPicker.vue";
import { isHomiiiAccommodation } from "@/Utils/homiiiContract";
import Button from "@/components/Ui/Button.vue";
import InitConversationModal from "@/pages/Conversation/InitConversationModal.vue";
import { saveRedirectUrl } from "@/Utils/Utils";
import RequestAccommodationVisit from "@/pages/AccommodationVisit/RequestAccommodationVisit.vue";
import FxCalendario from "@/components/DetalleAlojamiento/ficha/FxCalendario.vue";

export default {
    name: "BookingDetails",
	props: [
        "image", 
        "accommodation", 
    ],
    mixins: [ priceMixin ],
	data() {
		return {
			guest: 1,
			dates: [],
			datepickerValue: null,
			loadingCalendar: true,
			minNights: null,
            disabledDates: [],
            visible: false,
            showDateAlert: false,
            showIcalAlert: false,
            showPhoneAlert: false,
            accommodationIcal: null,
            showAvailabilityPopup: false,
            availabilityPopupMessage: '',
            availabilityPopupIcon: '',
            showSuccessPopup: false,
            successPopupMessage: '',
            phoneAlertMessage: this.$t('conversation.phone-required') || 'Por favor, registra un número de teléfono para continuar',

            showVisitModal: false,
            calAbierto: false,

            // Control to show/hide the initial Homiii snippet
            showHomiiiSnippet: true,

            // Reglas de contrato que Homiii solo expone por los mensajes de
            // error de comprobar_fechas: hasta cuándo está ocupada la habitación
            // y cuántos meses exige como mínimo (varía por habitación).
            homiiiMinMonths: 1,
            homiiiOccupiedUntil: null,

            _prevBodyOverflow: null,
            _prevHtmlOverflow: null,

            // Bloque `apt` del payload: disponibilidad de los departamentos de
            // APT, precalculada por el back a partir de FNS Manager. Null en
            // todo el resto del catálogo.
            apt: null,

            // Santiago Exchange: ventana disponible (`disponible_desde` /
            // `disponible_hasta`). El calendario ya viene bloqueado por las
            // restricciones de `sen:sync`; esto es solo para el aviso.
            sen: null,

            // Internal guard to avoid duplicate Homiii checks
            _lastHomiiiPayload: null,
		};
	},
	components: {
        FxCalendario,
        InitConversationModal,
        Button,
        DatePicker,
        HomiiiMonthPicker,
        Visit,
        RequestAccommodationVisit,
        Price,
        Guests,
		HotelDatePicker,
		Pay,
		Simulator,
	},
	methods: {
        logPriceProps(accommodation, monthlyPrice, dailyPrice) {
            return '';
        },

        handleVisitRequest() {
            if (this.isLoggedIn) {
                // Si está logueado, abrir modal con el formulario (sin redirección)
                this.showVisitModal = true;
            } else {
                // Usar el flujo existente que muestra el mensaje "need-login"
                this.loginBeforeSendMEssage();
            }
        },

        handleVisitSuccess() {
            this.showVisitModal = false;
            this.$toasted.show(this.$t('ask-visit.sent') || 'Solicitud enviada', {
                theme: "toasted-primary",
                position: "bottom-right",
                duration: 2500,
                className: "toasted"
            });
        },

		...mapActions(["cancelReservation", "checkUserData"]),
        ...mapActions("search", ["setCheckInDate", "setCheckOutDate"]),
        ...mapActions("simulator", ["setInitDate", "setEndDate"]),

        /**
         * Fechas elegidas en el calendario nuevo. Se guardan igual que lo hacía
         * DatePicker.vue (store search + simulator), así el simulador, Pay y
         * "Escríbele al anfitrión" siguen leyendo lo mismo. Van como Date al
         * mediodía para que la zona horaria no corra el día.
         */
        alCambiarFechas({ checkIn, checkOut }) {
            const aDate = v => { if (!v) return null; const [y, m, d] = v.split('-').map(Number); return new Date(y, m - 1, d, 12); };
            this.setCheckInDate(aDate(checkIn));
            this.setInitDate(aDate(checkIn));
            this.setCheckOutDate(aDate(checkOut));
            this.setEndDate(aDate(checkOut));
            if (checkIn && checkOut) this.calAbierto = false;
        },

        fechaLarga(v) {
            const [y, m, d] = String(v).slice(0, 10).split('-').map(Number);
            if (!y) return v;
            return new Date(y, m - 1, d, 12).toLocaleDateString(this.$i18n.locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }).replace(/\./g, '');
        },
        
		redirectToProfile() {
            this.$router.push({ name: 'editar-perfil' });
        },
        
		loadReservedDates() {
			this.loadingCalendar = true;

            const accommodationId = this.accommodationId;

            // First fetch accommodation meta (includes the apt block)
            axios
                .get(process.env.VUE_APP_API_URL + 'accommodations/getAccomodationFront/' + accommodationId)
                .then((value) => {
                    const accomodationData = value.data.accomodation;
                    this.minNights       = accomodationData.tiempo_min;
                    this.accommodationIcal = accomodationData.ical;
                    this.apt             = accomodationData.apt || null;
                    this.sen             = accomodationData.sen || null;

                    if (accomodationData.homiii_enabled) {
                        // Homiii no publica disponibilidad: hay que sondearla.
                        this.loadHomiiiRules();
                    } else if (this.apt) {
                        // APT: la disponibilidad ya viene precalculada en el
                        // payload, no hay que pedirle nada a nadie.
                        this.aplicarDisponibilidadApt();
                    } else {
                        // Normal mode: load unavailable dates as before
                        axios
                            .get(process.env.VUE_APP_API_URL + 'accommodations/unavailable_dates/' + accommodationId)
                            .then((response) => {
                                this.dates = response.data;
                            })
                            .finally(() => {
                                this.disabledDates = calculateOccupiedDays(this.dates, this.minNights);
                                this.loadingCalendar = false;
                            });
                    }
                })
                .catch(() => {
                    // Fallback: use normal unavailable dates
                    axios
                        .get(process.env.VUE_APP_API_URL + 'accommodations/unavailable_dates/' + accommodationId)
                        .then((response) => { this.dates = response.data; })
                        .finally(() => {
                            this.disabledDates = calculateOccupiedDays(this.dates, this.minNights);
                            this.loadingCalendar = false;
                        });
                });
		},

        /**
         * Reglas de contrato de la habitación en Homiii: hasta cuándo está
         * ocupada y cuántos meses exige como mínimo. El backend las deduce
         * sondeando comprobar_fechas y las cachea, porque Homiii no tiene
         * ningún endpoint que las devuelva.
         *
         * Si falla, el selector se queda con los valores por defecto (mínimo 1
         * mes, sin ocupación): las reglas de formato —día 1, último día del mes,
         * agosto— son fijas y se siguen aplicando, y la comprobación definitiva
         * la sigue haciendo checkHomiiiAvailability al cerrar el rango.
         */
        loadHomiiiRules() {
            const accommodationId = this.accommodationId;

            this.loadingCalendar = true;

            axios
                .get(`${process.env.VUE_APP_API_URL}homiii/availability/${accommodationId}`)
                .then((response) => {
                    const data = response.data || {};
                    if (!data.homiii_enabled) return;

                    this.homiiiMinMonths    = Number(data.meses_minimos) || 1;
                    this.homiiiOccupiedUntil = data.ocupado_hasta || null;
                })
                .catch((err) => {
                    console.error('No se pudieron cargar las reglas de Homiii', err);
                })
                .finally(() => {
                    this.loadingCalendar = false;
                });
        },

        /**
         * Aplica al calendario la disponibilidad de APT que ya viene en el
         * payload (bloque `apt`).
         *
         * No hay ninguna llamada a FNS acá. El back guarda una foto en
         * `apt_tipo_habitacion` / `apt_ocupacion` que refresca el comando
         * `apt:sync-disponibilidad`; preguntarle a FNS en vivo eran hasta 12
         * requests HTTP antes de poder pintar el calendario.
         *
         * OJO: la disponibilidad de FNS es por TIPO de departamento, no por
         * unidad. Los tramos ocupados quieren decir "no queda ningún studio
         * libre", no "este studio está ocupado".
         */
        aplicarDisponibilidadApt() {
            const apt = this.apt;
            if (!apt) return;

            const ocupados = [];

            // Los tramos vienen con fechas inclusivas: [desde, hasta].
            (apt.ocupaciones || []).forEach((tramo) => {
                if (!tramo.desde || !tramo.hasta) return;

                const cursor = new Date(tramo.desde + 'T00:00:00');
                const fin    = new Date(tramo.hasta + 'T00:00:00');

                while (cursor <= fin) {
                    ocupados.push(this.toIsoDate(cursor));
                    cursor.setDate(cursor.getDate() + 1);
                }
            });

            // Todo lo anterior a `disponible_desde` tampoco es reservable: el
            // horizonte que consultó el sync arranca hoy, así que los días de
            // acá al primer día libre hay que bloquearlos a mano.
            if (apt.disponible_desde) {
                const desde  = new Date(apt.disponible_desde + 'T00:00:00');
                const cursor = new Date();
                cursor.setHours(0, 0, 0, 0);

                while (cursor < desde) {
                    ocupados.push(this.toIsoDate(cursor));
                    cursor.setDate(cursor.getDate() + 1);
                }
            }

            this.dates         = ocupados;
            this.disabledDates = calculateOccupiedDays(ocupados, this.minNights);
            this.loadingCalendar = false;
        },

        /**
         * If the accommodation belongs to Homiii (host id 6694) and both dates
         * are selected, call backend proxy to check availability on Homiii.
         */
        async checkHomiiiAvailability() {
            if (!this.checkInDate || !this.checkOutDate) return;
            if (!this.accommodation || !this.accommodation.idcodigo) return;
            if (!this.isHomiiiHost) return;

            // use the YYYY-MM-DD strings already stored in Vuex to avoid timezone shifts
            const payload = {
                id_habitacion: this.accommodation.idcodigo,
                fecha_entrada: this.checkInDate,
                fecha_salida: this.checkOutDate,
            };

            // Client-side validation: avoid calling backend when dates are invalid
            const todayStr = this.toIsoDate(new Date());
            if (payload.fecha_entrada < todayStr) {
                this.$store.dispatch('simulator/setAvailable', false);
                this.showAvailabilityPopup = true;
                this.availabilityPopupMessage = 'La fecha de entrada no puede ser inferior a la fecha actual';
                this.availabilityPopupIcon = 'fas fa-calendar-times text-danger';
                return;
            }
            if (payload.fecha_salida <= payload.fecha_entrada) {
                this.$store.dispatch('simulator/setAvailable', false);
                this.showAvailabilityPopup = true;
                this.availabilityPopupMessage = 'La fecha de salida debe ser posterior a la fecha de entrada';
                this.availabilityPopupIcon = 'fas fa-calendar-times text-danger';
                return;
            }

            const payloadKey = payload.id_habitacion + '|' + payload.fecha_entrada + '|' + payload.fecha_salida;
            if (this._lastHomiiiPayload === payloadKey) return; // already checked
            this._lastHomiiiPayload = payloadKey;

            this.showAvailabilityPopup = true;
            this.availabilityPopupMessage = this.$t('conversation.checking-availability') || 'Comprobando disponibilidad...';
            this.availabilityPopupIcon = 'fas fa-spinner fa-spin text-primary';

            try {
                const response = await axios.post(`${process.env.VUE_APP_API_URL}homiii/check-dates`, payload);
                const data = response.data || {};

                const disponible = (data.disponible === true || data.disponible === 'true' || data.disponible === 1 || data.available === true);

                // Update simulator available flag so Pay.vue can enable/disable reserve
                this.$store.dispatch('simulator/setAvailable', !!disponible);

                // Hide the Homiii snippet as we are showing a response
                this.showHomiiiSnippet = false;

                if (disponible) {
                    this.availabilityPopupMessage = 'Disponible';
                    this.availabilityPopupIcon = 'success';
                    setTimeout(() => { this.showAvailabilityPopup = false; }, 1200);
                    return;
                }

                // Not available
                let message = data.mensaje || data.message || 'No disponible para las fechas seleccionadas.';
                if (data.proxima_disponibilidad) {
                    message += '\nPróxima disponibilidad: ' + data.proxima_disponibilidad;
                }

                // Si Homiii dice hasta cuándo está reservada, se repinta el
                // calendario con esa fecha: la ocupación cacheada quedó vieja.
                const reservada = /reservada[^\d]*(\d{4}-\d{2}-\d{2})/i.exec(message);
                if (reservada) {
                    this.homiiiOccupiedUntil = reservada[1];
                }

                this.availabilityPopupMessage = message;
                this.availabilityPopupIcon = 'fas fa-calendar-times text-danger';
            } catch (err) {
                console.error('Homiii check error', err);
                // Ensure reserve stays disabled on error
                this.$store.dispatch('simulator/setAvailable', false);
                // Hide the Homiii snippet on error as well
                this.showHomiiiSnippet = false;
                this.availabilityPopupMessage = 'Error comprobando disponibilidad';
                this.availabilityPopupIcon = 'fas fa-exclamation-triangle text-warning';
            }
        },

        closeAvailabilityPopup() {
            this.showAvailabilityPopup = false;
            // also hide the snippet after closing
            this.showHomiiiSnippet = false;
        },

        /**
         * Always returns YYYY-MM-DD regardless of input format.
         * Kept separate from the existing formatDate to avoid conflicts.
         */
        toIsoDate(date) {
            if (!date) return null;
            const d = new Date(date);
            const yyyy = d.getFullYear();
            const mm   = String(d.getMonth() + 1).padStart(2, '0');
            const dd   = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        },

        loginBeforeSendMEssage () {
            
            // Save the url the user was seeing
            saveRedirectUrl(this.$route);
            
            this.$toasted.show(this.$t('login.need-login'), {
                theme: "toasted-primary",
                position: "bottom-right",
                duration: 2500,
                className: "toasted"
            });
            
            setTimeout(() => {
                this.$router.push({ name: "login" });
            }, 2500);
            
        },

        async handleInitConversation() {
            // Validar si el usuario tiene número de teléfono
            if (!this.user.telefono || this.user.telefono === "null") {
                this.showPhoneAlert = true; // Mostrar alerta de número de teléfono
                return;
            }

            // Validar si el número de teléfono contiene caracteres no permitidos (solo dígitos permitidos)
            const invalidPhoneRegex = /[^\d]/; // Permitir solo dígitos
            if (invalidPhoneRegex.test(this.user.telefono)) {
                this.showPhoneAlert = true; // Mostrar alerta de número de teléfono
                this.$set(this, 'phoneAlertMessage', this.$t('conversation.invalid-phone'));
                return;
            }

            // Validar si las fechas están seleccionadas
            if (!this.checkInDate || !this.checkOutDate) {
                this.showDateAlert = true; // Mostrar alerta de fechas
                return;
            }

            // Mostrar popup de "Consultando disponibilidad"
            this.showAvailabilityPopup = true;
            this.availabilityPopupMessage = this.$t('conversation.checking-availability');
            this.availabilityPopupIcon = 'fas fa-spinner fa-spin text-primary';

            // Validar el valor de accommodationIcal
            const icalValue = Number(this.accommodationIcal);

            // Validar y asignar valores seguros para el mensaje
            const checkInFormatted = this.checkInDate ? this.formatDate(this.checkInDate) : 'N/A';
            const checkOutFormatted = this.checkOutDate ? this.formatDate(this.checkOutDate) : 'N/A';

            const accommodationLink = this.accommodation && this.accommodation.idalojamiento
                ? `${window.location.origin}/accommodation/${this.accommodation.idalojamiento}`
                : 'N/A';

            if (icalValue === 1) {
                try {
                    // Mensaje automático del guest
                    const guestMessage = this.$t('conversation.auto-message', {
                        name: this.user.nombre || 'Usuario',
                        checkIn: checkInFormatted,
                        checkOut: checkOutFormatted
                    });

                    // Mensaje automático del host
                    const hostMessage = this.$t('conversation.auto-message-ical', {
                        accommodationLink: accommodationLink,
                        checkIn: checkInFormatted,
                        checkOut: checkOutFormatted
                    }).replace(/<[^>]*>/g, '');

                    // Enviar ambos mensajes al backend
                    const response = await axios.post(
                        `${process.env.VUE_APP_API_URL}conversations`,
                        {
                            host_id: parseInt(this.accommodation.host_id, 10),
                            guest_id: parseInt(this.user.idusuario, 10),
                            accommodation_id: this.accommodation.idalojamiento,
                            guest_message: guestMessage,
                            host_message: hostMessage,
                            check_in_date: checkInFormatted,
                            check_out_date: checkOutFormatted,
                            ical: this.accommodationIcal
                        }
                    );

                    if (response.status === 201) {
                        this.$store.dispatch('fetchConversations');
                        this.successPopupMessage = this.$t('conversation.availability-success');
                        this.showSuccessPopup = true;
                        this.showAvailabilityPopup = false;
                        setTimeout(() => {
                            this.showSuccessPopup = false;
                            this.$router.push({ name: 'conversation' }).catch(() => {
                                this.$router.push({ name: 'messages' });
                            });
                        }, 3000);
                    } else {
                        console.log('Estado inesperado:', response.status);
                    }
                } catch (error) {
                    console.error('Error enviando mensajes automáticos:', error);
                    this.$toasted.show('Error al enviar el mensaje', {
                        theme: "toasted-primary",
                        position: "bottom-right",
                        duration: 2000,
                        className: "toasted"
                    });
                    this.showIcalAlert = true;
                    this.showAvailabilityPopup = false;
                }
                return;
            }

            if (icalValue === 0) {
                try {
                    // Validar valores requeridos antes de continuar
                    if (!this.user || !this.user.idusuario) {
                        console.error('El usuario no está definido o no tiene un ID válido.');
                        this.$toasted.show('Error: Usuario no válido.', {
                            theme: "toasted-primary",
                            position: "bottom-right",
                            duration: 2000,
                            className: "toasted"
                        });
                        this.showAvailabilityPopup = false;
                        return;
                    }

                    if (!this.accommodation || !this.accommodation.idalojamiento) {
                        console.error('El alojamiento no está definido o no tiene un ID válido.');
                        this.$toasted.show('Error: Alojamiento no válido.', {
                            theme: "toasted-primary",
                            position: "bottom-right",
                            duration: 2000,
                            className: "toasted"
                        });
                        this.showAvailabilityPopup = false;
                        return;
                    }

                    const automaticMessage = this.$t('conversation.auto-message', {
                        name: this.user.nombre || 'Usuario',
                        checkIn: checkInFormatted,
                        checkOut: checkOutFormatted
                    });

                    // Enviar el mensaje usando el endpoint existente
                    const response = await axios.post(
                        `${process.env.VUE_APP_API_URL}conversations`,
                        {
                            guest_id: parseInt(this.user.idusuario, 10), // Convertir a entero
                            accommodation_id: this.accommodation.idalojamiento,
                            message: automaticMessage,
                            check_in_date: checkInFormatted, // Formato YYYY/MM/DD
                            check_out_date: checkOutFormatted, // Formato YYYY/MM/DD
                            ical: this.accommodationIcal // Obtener el valor de la API
                        }
                    );

                    console.log('Datos enviados al backend:', {
                        guest_id: parseInt(this.user.idusuario, 10),
                        accommodation_id: this.accommodation.idalojamiento,
                        message: automaticMessage,
                        check_in_date: checkInFormatted,
                        check_out_date: checkOutFormatted,
                        ical: this.accommodationIcal
                    });

                    if (response.status === 201) {
                        // Actualizar la lista de conversaciones o mensajes
                        this.$store.dispatch('fetchConversations');

                        // Mostrar el nuevo popup de éxito
                        this.successPopupMessage = this.$t('conversation.availability-success');
                        this.showSuccessPopup = true;

                        // Asegurarse de que el popup de disponibilidad se oculte
                        this.showAvailabilityPopup = false;

                        // Asegurarse de que el popup permanezca visible antes de redirigir
                        setTimeout(() => {
                            this.showSuccessPopup = false; // Ocultar el popup
                            this.$router.push({ name: 'conversation' }).catch(() => {
                                this.$router.push({ name: 'messages' });
                            });
                        }, 3000); // Incrementar el tiempo de espera a 3 segundos
                    } else {
                        console.log('Estado inesperado:', response.status);
                    }
                } catch (error) {
                    console.error('Error creando conversación automática:', error);
                    this.$toasted.show('Error al enviar el mensaje', {
                        theme: "toasted-primary",
                        position: "bottom-right",
                        duration: 2000,
                        className: "toasted"
                    });
                    this.showIcalAlert = true;
                    this.showAvailabilityPopup = false;
                }
                return;
            }

            // Manejar casos inesperados
            console.warn('Valor inesperado de accommodationIcal:', icalValue);
            this.showAvailabilityPopup = false;
            this.$toasted.show('Error inesperado. Por favor, inténtalo de nuevo.', {
                theme: "toasted-primary",
                position: "bottom-right",
                duration: 2000,
                className: "toasted"
            });
        },

        async createAutoConversation() {
            try {
                // Format dates for the message
                const checkInFormatted = this.formatDate(this.checkInDate).replace(/-/g, '/'); // Cambiar formato a YYYY/MM/DD
                const checkOutFormatted = this.formatDate(this.checkOutDate).replace(/-/g, '/'); // Cambiar formato a YYYY/MM/DD
                const userName = this.user.nombre || 'Usuario';

                // Usa $t para traducir el mensaje según el idioma seleccionado
                const automaticMessage = this.$t('conversation.auto-message', {
                    name: userName,
                    checkIn: checkInFormatted,
                    checkOut: checkOutFormatted
                });

                // Send the message using existing startConversation endpoint
                const response = await axios.post(
                    `${process.env.VUE_APP_API_URL}conversations`,
                    {
                        guest_id: parseInt(this.user.idusuario, 10), // Convertir a entero
                        accommodation_id: this.accommodation.idalojamiento,
                        message: automaticMessage,
                        check_in_date: checkInFormatted, // Formato YYYY/MM/DD
                        check_out_date: checkOutFormatted, // Formato YYYY/MM/DD
                    }
                );

                if (response.status === 201) {
                    // Show success message
                    this.$toasted.show('Mensaje enviado correctamente', {
                        theme: "toasted-primary",
                        position: "bottom-right",
                        duration: 2000,
                        className: "toasted"
                    });

                    // Redirect to conversations page after a short delay
                    setTimeout(() => {
                        this.$router.push({ name: 'conversation' }).catch(() => {
                            // Fallback to messages if conversations route doesn't exist
                            this.$router.push({ name: 'messages' });
                        });
                    }, 1000);
                }
            } catch (error) {
                console.error('Error creando conversación automática:', error);

                // Show error message
                this.$toasted.show('Error al enviar el mensaje', {
                    theme: "toasted-primary",
                    position: "bottom-right",
                    duration: 2000,
                    className: "toasted"
                });

                // Fallback to showing the modal if API fails
                this.showIcalAlert = true;
            }
        },

        formatDate(date) {
            if (!date) {
                console.warn('Fecha no válida proporcionada:', date);
                return '';
            }

            const parsedDate = new Date(date);
            if (isNaN(parsedDate)) {
                console.error('No se pudo analizar la fecha:', date);
                return 'Fecha inválida';
            }

            return parsedDate.toLocaleDateString('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },

        handleDateSelection(selectedDate) {
            // Aquí puedes agregar la lógica para manejar la selección de fechas

            // Por ejemplo, podrías guardar la fecha seleccionada en una variable local o realizar validaciones
            this.datepickerValue = selectedDate;
        }
    },

    watch: {
        checkInDate(newVal) {
            // Re-evaluate whether to show the Homiii snippet (show when any date missing)
            this.showHomiiiSnippet = !(newVal && this.checkOutDate);

            if (this.isHomiiiHost) {
                this.checkHomiiiAvailability();
            }
        },
        checkOutDate(newVal) {
            // Re-evaluate whether to show the Homiii snippet (show when any date missing)
            this.showHomiiiSnippet = !(this.checkInDate && newVal);

            if (this.isHomiiiHost) {
                this.checkHomiiiAvailability();
            }
        },
        showVisitModal(isOpen) {
            if (typeof document === 'undefined') return;
            const body = document.body;
            const html = document.documentElement;
            if (!body || !html) return;

            if (isOpen) {
                this._prevBodyOverflow = body.style.overflow;
                this._prevHtmlOverflow = html.style.overflow;
                body.style.overflow = 'hidden';
                html.style.overflow = 'hidden';
            } else {
                body.style.overflow = this._prevBodyOverflow || '';
                html.style.overflow = this._prevHtmlOverflow || '';
                this._prevBodyOverflow = null;
                this._prevHtmlOverflow = null;
            }
        }
    },

    computed: {
        ...mapState(["reservation", "user"]),
        ...mapState('search', ["checkInDate", "checkOutDate"]),
        ...mapGetters(["isLoggedIn"]),

        /**
         * Id del alojamiento para pegarle a la API. Sale del alojamiento ya
         * cargado y no del parámetro de la URL, porque esta puede venir con el
         * `idcodigo`: /alojamiento/{id} y /alojamiento/{idcodigo} llevan los dos
         * a la misma ficha, pero el resto de los endpoints esperan el id.
         */
        accommodationId() {
            if (this.accommodation && this.accommodation.idalojamiento) {
                return this.accommodation.idalojamiento;
            }

            return this.$route.params.idalojamiento;
        },

        isHomiiiHost() {
            return isHomiiiAccommodation(this.accommodation);
        },

        /**
         * Fecha desde la que se puede reservar, para el inventario que llega
         * con disponibilidad precalculada y todavía está ocupado: Spacest y APT.
         * Devuelve null si no es ninguno de los dos o si ya está libre, y en ese
         * caso no se muestra nada.
         *
         * En APT la fecha es por TIPO de departamento: "desde cuándo hay un
         * studio libre", no "desde cuándo se libera este studio".
         */
        disponibleDesdeTexto() {
            const bloque = (this.accommodation && this.accommodation.spacest) || this.apt || this.sen;
            if (!bloque || !bloque.disponible_desde) return null;

            const desde = new Date(bloque.disponible_desde + 'T00:00:00');
            if (isNaN(desde.getTime()) || desde <= new Date()) return null;

            return desde.toLocaleDateString(this.$i18n && this.$i18n.locale ? this.$i18n.locale : 'es', {
                day: 'numeric', month: 'long', year: 'numeric',
            });
        },

    },
    created() {
        // Fechas que llegan en la URL (/alojamiento/:id/:fecha_inicio/:fecha_termino):
        // antes las pasaba al store el DatePicker al montarse.
        if (!this.isHomiiiHost) {
            const ini = this.$route.params.fecha_inicio || this.$route.query.fecha_inicio;
            const fin = this.$route.params.fecha_termino || this.$route.query.fecha_termino;
            if (ini) { this.setCheckInDate(ini); this.setInitDate(ini); }
            if (fin) { this.setCheckOutDate(fin); this.setEndDate(fin); }
        }
        this.loadReservedDates();
    },
    beforeDestroy() {
        // Asegurar que el scroll se restaure si el componente se destruye con el modal abierto
        if (this.showVisitModal) {
            this.showVisitModal = false;
        }
    }
};
</script>


<!-- Estilos: components/DetalleAlojamiento/ficha/ficha.css (los carga la ficha) -->
